"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, CircleCheck as CheckCircle2, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  id: "permits",
  question: "Do I need city permits for structural changes in Brookhaven?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Yes. The City of Brookhaven Community Development Department requires building permits for any structural modifications, load-bearing wall removals, major plumbing relocations, and new electrical circuits.
        </p>
        <p>
          We manage the complete municipal permitting workflow on your behalf, including:
        </p>
        <ul className="space-y-1.5 list-none pl-1">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>Structural engineering reviews and stamped architectural plan submission</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>Coordination of all required rough-in and final city building inspections</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>DeKalb County utility tap and sewer compliance when altering footprints</span>
          </li>
        </ul>
      </div>
}, {
  id: "living-during-work",
  question: "Can we stay in our home while interior work is underway?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Most homeowners remain in their residence during single-space renovations like a kitchen or primary bathroom remodel. For whole-home reconfigurations, staying off-site is often more comfortable.
        </p>
        <p>
          When you reside on-site, we maintain strict livability standards:
        </p>
        <ul className="space-y-1.5 list-none pl-1">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>Floor-to-ceiling zippered plastic dust containment barriers</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>Commercial HEPA air scrubbers running throughout active demolition and sanding</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>Daily broom-clean sweeps and end-of-day tool consolidation</span>
          </li>
        </ul>
      </div>
}, {
  id: "timeline",
  question: "What is the typical timeline for an interior remodel?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Schedules depend on scope, structural requirements, and lead times for custom millwork:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          <div className="rounded-md border border-border bg-card p-3">
            <div className="font-semibold text-foreground text-sm">Primary Bathroom</div>
            <div className="text-xs text-primary font-medium mt-0.5">4 to 6 weeks</div>
            <p className="text-xs text-muted-foreground mt-1.5">Tile, custom vanity, glass enclosure, plumbing trims.</p>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="font-semibold text-foreground text-sm">Custom Kitchen</div>
            <div className="text-xs text-primary font-medium mt-0.5">7 to 10 weeks</div>
            <p className="text-xs text-muted-foreground mt-1.5">Cabinet fabrication, stone templating, appliances.</p>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="font-semibold text-foreground text-sm">Whole Interior</div>
            <div className="text-xs text-primary font-medium mt-0.5">3 to 5 months</div>
            <p className="text-xs text-muted-foreground mt-1.5">Load-bearing beam installation, flooring, full trims.</p>
          </div>
        </div>
      </div>
}, {
  id: "change-orders",
  question: "How do you handle unforeseen conditions and change orders?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Older homes in historic Brookhaven or Ashford Park sometimes reveal legacy plumbing or framing issues once drywall is removed. We operate on a strict no-surprise policy.
        </p>
        <p>
          If unexpected conditions arise or you choose to upgrade a material:
        </p>
        <ul className="space-y-1.5 list-none pl-1">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>We document the condition with photos and explain practical repair options</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>You receive a line-item written change order detailing exact costs and calendar impact</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>No work proceeds on that adjustment without your written digital authorization</span>
          </li>
        </ul>
      </div>
}, {
  id: "design-help",
  question: "Do you help with interior design decisions and material selections?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Yes. Our comprehensive design-build process ensures every aesthetic decision aligns with your budget and structural blueprint before construction begins.
        </p>
        <p>
          We guide you step-by-step through our curated design service:
        </p>
        <ul className="space-y-1.5 list-none pl-1">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>Full 3D digital renderings so you can visualize layouts, cabinetry, and sightlines</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>Accompanied showroom appointments in Atlanta for slabs, tile, plumbing fixtures, and lighting</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
            <span>Complete finish schedule documenting exact paint codes, hardware finishes, and grout selections</span>
          </li>
        </ul>
      </div>
}];
export default function RemodelingFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section id="remodelingfaq" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-opacity duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Heading, Supporting text, and Featured Project Photo */}
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                <HelpCircle className="size-4" />
                <span>Frequently asked questions</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold tracking-tight text-foreground">
                Clear answers for your Brookhaven interior renovation
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                Remodeling your home involves major decisions. Here is straightforward guidance on municipal permits, construction timelines, and everyday project expectations.
              </p>
            </div>

            <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_detail.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_detail.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_detail.webp-wvc-sizes" alt="Completed open-concept kitchen and living interior renovation in Brookhaven" loading="lazy" decoding="async" className="w-full h-56 sm:h-64 object-cover" />
              <div className="p-4 sm:p-5 space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Local Experience
                </div>
                <p className="text-sm font-medium text-foreground">
                  Have questions about your specific floor plan or Brookhaven zoning requirements?
                </p>
                <div className="pt-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-2">
                      <PhoneCall className="size-3.5" />
                      <span>Speak with our team</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Accordion */}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border rounded-lg shadow-sm p-2 sm:p-4 md:p-6">
              <Accordion type="single" collapsible defaultValue="permits" className="w-full">
                {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-b border-border last:border-b-0 py-1">
                    <AccordionTrigger className="text-base sm:text-lg font-serif font-medium text-foreground hover:no-underline hover:text-primary py-4 text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}